<script>
import { AbcHeading, AbcIcon } from "@ordbogen-npm/abc-dsk";
import { isEmpty } from "lodash-es";
import { mapStores } from "pinia";
import { defineComponent } from "vue";
import { RouterLink } from "vue-router";

import TgIconButton from "@/abc-dsk/components/IconButton/TgIconButton.vue";
import { isValidationFailedResponse } from "@/common/api/api.service";
import VocabService from "@/common/api/vocab.service";
import { useVocabAchievements } from "@/common/composables/use-vocab-achievements";
import { LANGUAGE_GROUP_ENGLISH, LANGUAGE_GROUP_GERMAN } from "@/common/language-groups";
import NotificationService from "@/common/notification.service";
import { WordListType } from "@/common/types/vocab";
import { flushVueQueue } from "@/common/utils";
import { filterAchievementCollection, ModalType } from "@/common/vocabulary-trainer-helpers";
import IconPlus from "@/components/icons/IconPlus.vue";
import CreateOrAddListModal from "@/components/modals/vocabularyTrainer/CreateOrAddListModal.vue";
import AchievementSimpleItem from "@/components/vocabularyTrainer/AchievementSimpleItem.vue";
import WordListContainer from "@/components/vocabularyTrainer/WordListContainer.vue";
import { useAuthStore, useSettingsStore } from "@/stores";

const { achievementHasMultipleTiers, setAchievements } = useVocabAchievements();

export default defineComponent({
  name: "VocabHomeView",
  components: {
    AbcHeading,
    AbcIcon,
    TgIconButton,
    IconPlus,
    WordListContainer,
    CreateOrAddListModal,
    AchievementSimpleItem,
    RouterLink
  },
  data() {
    return {
      wordLists: [],
      achievements: [],
      isModalOpen: false,
      modalInputValue: "",
      modalListName: "",
      pollInterval: null
    };
  },
  computed: {
    ...mapStores(useAuthStore, useSettingsStore),

    activeLanguageGroup() {
      return this.settingsStore.activeLanguageGroup;
    },

    sortedWordLists() {
      return [...this.wordLists].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },
    publicWordLists() {
      return this.sortedWordLists.filter((wl) => wl.type === WordListType.Public);
    },
    privateWordLists() {
      return this.sortedWordLists.filter((wl) => wl.type === WordListType.Private);
    },

    sortedAchievements() {
      const unopenedAchievements = this.achievements.filter((a) => !a.badge?.openedAt);

      return filterAchievementCollection(unopenedAchievements)
        .sort((a, b) => {
          if (a.achievedAt && b.achievedAt) {
            return a.steps - b.steps;
          }

          if (a.achievedAt) {
            return -1;
          }

          if (b.achievedAt) {
            return 1;
          }

          return b.progress / b.steps - a.progress / a.steps;
        })
        .slice(0, 5);
    },

    addButtonLabel() {
      return this.authStore.userIsTeacher
        ? this.$t("vocab.home.createList")
        : this.$t("vocab.home.addList");
    },

    modalTitle() {
      if (this.$route.query.code) {
        return this.$t("vocab.home.modal.add.title");
      }

      return this.authStore.userIsTeacher
        ? this.$t("vocab.home.modal.create.title")
        : this.$t("vocab.home.modal.add.title");
    },
    modalBodyText() {
      if (this.modalListName !== "") {
        return this.$t("vocab.home.modal.add.inviteText");
      }

      return this.authStore.userIsTeacher ? "" : this.$t("vocab.home.modal.add.bodyText");
    },
    modalInputPlaceholder() {
      return this.authStore.userIsTeacher
        ? this.$t("vocab.home.modal.create.inputPlaceholder")
        : this.$t("vocab.home.modal.add.inputPlaceholder");
    }
  },
  watch: {
    activeLanguageGroup(value) {
      if (![LANGUAGE_GROUP_ENGLISH, LANGUAGE_GROUP_GERMAN].includes(value)) {
        this.$router.push({ name: "vocab-unavailable" });
        return;
      }

      this.updateList();
    }
  },
  async created() {
    await this.updateList();

    // Make sure component is rendered before possibly showing add list modal.
    // Might be fine to remove after modal design is applied, please test
    await flushVueQueue();

    const { code, name } = this.$route.query;
    if (code && name) {
      await this.handleListFromLink(code, name);
    }
  },
  async mounted() {
    this.pollInterval = setInterval(() => this.updateList(), 5000);

    // No need to make request for data that are never shown to a teacher.
    if (!this.authStore.userIsTeacher) {
      this.achievements = (await VocabService.getAchievements()).data.data;
      setAchievements(this.achievements);
    }
  },
  unmounted() {
    clearInterval(this.pollInterval);
  },
  methods: {
    achievementHasMultipleTiers,

    async updateList() {
      const response = await VocabService.getLists(this.activeLanguageGroup);
      this.wordLists = response.data.data;
    },

    onAddOrCreateClick() {
      this.isModalOpen = true;
    },

    onModalSubmit({ input, type }) {
      if (type === ModalType.Create) {
        this.createList(input);
      } else {
        this.addListFromCode(input);
      }

      this.onModalClose();
    },

    async createList(name) {
      try {
        const response = await VocabService.createList(name, this.activeLanguageGroup);
        this.wordLists.push(response.data.data);
        NotificationService.success("Listen blev oprettet");
      } catch (error) {
        if (isValidationFailedResponse(error.response)) {
          NotificationService.error("Ugyldigt navn");
        } else {
          throw error;
        }
      }
    },

    handleListFromLink(shareCode, name) {
      this.modalInputValue = decodeURIComponent(shareCode);
      this.modalListName = decodeURIComponent(name);
      this.isModalOpen = true;
    },

    async addListFromCode(shareCode) {
      const response = await VocabService.addList(shareCode);
      if (response.status === 201) {
        const listData = response.data.data;

        if (listData.language !== this.activeLanguageGroup) {
          this.settingsStore.setActiveLanguageGroup(listData.language);
        } else {
          this.wordLists.push(listData);
        }
      }

      NotificationService.success("Listen blev tilføjet");
    },

    onModalClose() {
      this.isModalOpen = false;
      this.modalListName = "";
      this.modalInputValue = "";

      if (!isEmpty(this.$route.query)) {
        this.$router.replace({ query: {} });
      }
    }
  }
});
</script>

<template>
  <main class="vocab-home container" v-bind="$attrs">
    <div class="row">
      <div
        :class="{
          'col-12 col-md-8 col-xl-9': !authStore.userIsTeacher,
          'col-12': authStore.userIsTeacher
        }"
      >
        <section class="row">
          <div class="col-12">
            <AbcHeading id="my-lists-heading" size="md" tag="h1">
              {{ $t("vocab.home.myLists") }}
            </AbcHeading>
            <ul class="list-unstyled" aria-labelledby="my-lists-heading">
              <li v-for="(list, index) in privateWordLists" :key="index">
                <WordListContainer :can-start-quiz="list.words.length >= 5" :list="list" />
              </li>
            </ul>
            <div class="vocab-home__add-container">
              <TgIconButton
                id="vocab-home-add-or-create"
                variant="solid"
                size="md"
                @click="onAddOrCreateClick()"
                @close="isModalOpen = false"
              >
                <IconPlus role="presentation" />
              </TgIconButton>
              <label for="vocab-home-add-or-create" class="vocab-home__add-label m-0">{{
                addButtonLabel
              }}</label>
            </div>
          </div>
        </section>
        <div class="spacer"></div>
        <section class="row">
          <div class="col-12">
            <AbcHeading id="public-lists-heading" size="md" tag="h1">
              {{ $t("vocab.home.publicLists") }}
            </AbcHeading>
            <ul class="list-unstyled" aria-labelledby="public-lists-heading">
              <li v-for="(list, index) in publicWordLists" :key="index">
                <WordListContainer :list="list" />
              </li>
            </ul>
          </div>
        </section>
      </div>
      <div v-if="!authStore.userIsTeacher" class="col-md-4 col-xl-3 d-none d-md-block">
        <div
          aria-hidden="true"
          class="welcome-card d-flex align-items-center justify-content-between"
        >
          <div>
            <p class="welcome-card__text">{{ $t("vocab.home.sidebar.welcomeBack") }}</p>
            <p class="welcome-card__text welcome-card__text--prominent">
              {{ authStore.user.name }}
            </p>
          </div>
          <div class="welcome-card__icon">
            <AbcIcon name="lightning" color="secondary" />
          </div>
        </div>

        <div class="achievements-widget">
          <div class="achievements-widget__top">
            <p class="achievements-widget__text">{{ $t("vocab.home.sidebar.missionsHeading") }}</p>
          </div>
          <hr class="achievements-widget__spacer" />
          <div class="achievements-widget__content">
            <template v-for="(achievement, index) in sortedAchievements" :key="index">
              <RouterLink
                :to="{ name: 'vocab.achievements' }"
                class="achievements-widget__item-link"
              >
                <AchievementSimpleItem
                  :achievement="achievement"
                  :show-steps-in-name="achievementHasMultipleTiers(achievement)"
                />
              </RouterLink>
              <hr class="achievements-widget__spacer" />
            </template>
          </div>
          <RouterLink class="achievements-widget__cta-link" :to="{ name: 'vocab.achievements' }">
            {{ $t("vocab.home.sidebar.missionsLink") }}
          </RouterLink>
        </div>
      </div>
    </div>
  </main>

  <CreateOrAddListModal
    v-model="modalInputValue"
    :is-open="isModalOpen"
    :title="modalTitle"
    :body-text="modalBodyText"
    :input-placeholder="modalInputPlaceholder"
    :list-name="modalListName"
    :show-input="modalListName === ''"
    :show-name="modalListName !== ''"
    @close="onModalClose()"
    @submit="onModalSubmit($event)"
  />
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.vocab-home {
  margin-bottom: 1.5rem;
  padding-top: 22px;
}

.vocab-home__add-container {
  display: flex;
  gap: 13px;
  align-items: center;
  margin-top: 28px;
}

.vocab-home__add-label {
  cursor: pointer;
}

.spacer {
  height: 60px;
}

.welcome-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 20px;
  border-radius: 8px;
  background-color: @colors[@white];
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  gap: 10px;

  &__text {
    margin-bottom: 0;
    color: rgb(@theme-colors[@grey][base]);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;

    &--prominent {
      color: rgb(@theme-colors[@grey][110]);
      font-weight: 500;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background-color: rgb(@theme-colors[@secondary][10]);
  }
}

.achievements-widget {
  border-radius: 8px;
  background-color: @colors[@white];
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

  &__top {
    padding: 12px 20px;
  }

  &__spacer {
    margin: 0;
    border-bottom: 1px rgb(@theme-colors[@grey][50]);
  }

  &__text {
    margin-bottom: 0;
    color: rgb(@theme-colors[@grey][base]);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
  }

  &__item-link {
    color: @colors[@black];

    &:hover {
      text-decoration: none;
    }
  }

  &__cta-link {
    display: block;
    padding: 20px;
    transition: color 150ms ease-in-out;
    color: rgb(@theme-colors[@grey][base]);
    font-size: 1rem;
    font-weight: 600;
    letter-spacing: 0.0688rem;
    line-height: 1.1875rem;
    text-align: center;
    text-decoration: none;
    text-transform: uppercase;

    &:hover {
      color: rgb(@theme-colors[@grey][110]);
    }
  }
}
</style>
